import { useEffect, useRef, useState } from 'react';
import { getJustPulled, type ApiClient, type JustPulledItem } from '../api';

export interface UseJustPulledOptions {
  /** How often to refetch, in ms. Default 30 000. Pass 0 to disable. */
  pollIntervalMs?: number;
  /** Page size. Default 32. */
  per?: number;
  /** Limit the feed to a single pack (pool_offer.id). Omit / null →
   *  global feed across all packs. */
  poolOfferId?: number | string | null;
}

export interface UseJustPulledResult {
  items: JustPulledItem[];
  loading: boolean;
}

/**
 * Live activity feed for the Just Pulled strip — initial fetch on mount
 * + setInterval refresh. Soft-fails: on error keeps the previous items
 * so a transient network blip doesn't blank the strip.
 */
export function useJustPulled(
  client: ApiClient,
  options: UseJustPulledOptions = {},
): UseJustPulledResult {
  const { pollIntervalMs = 30_000, per = 32, poolOfferId = null } = options;
  const [items, setItems] = useState<JustPulledItem[]>([]);
  const [loading, setLoading] = useState(true);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    let initial = true;
    // Reset items+loading whenever the pack scope changes — otherwise
    // switching to a new pack briefly shows the previous pack's pulls.
    setItems([]);
    setLoading(true);

    const fetchOnce = async () => {
      try {
        const page = await getJustPulled(client, {
          per,
          ...(poolOfferId !== null && poolOfferId !== '' ? { poolOfferId } : {}),
        });
        if (cancelledRef.current) return;
        setItems(page.items);
      } catch {
        // Soft fail — keep previous items, don't show errors in a
        // decorative strip.
      } finally {
        if (!cancelledRef.current && initial) {
          setLoading(false);
          initial = false;
        }
      }
    };

    void fetchOnce();

    if (pollIntervalMs <= 0) {
      return () => {
        cancelledRef.current = true;
      };
    }

    const id = window.setInterval(() => {
      void fetchOnce();
    }, pollIntervalMs);

    return () => {
      cancelledRef.current = true;
      window.clearInterval(id);
    };
  }, [client, pollIntervalMs, per, poolOfferId]);

  return { items, loading };
}
